import React, { ReactNode, forwardRef, useCallback, useContext, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { generateLogzTestAttributes } from '@logz-pkg/test-selectors';
import { ModalContext } from '../Modal';
import { PopoverArrow, PopoverContainer } from './popover.styles';
import { usePopoverOnClick, usePopoverOnHover, usePopoverOnResize } from './utilities.hook';
import { popoverConfig } from './popover.config';
import { usePopoverState } from './Popover.provider';
import type { FlipOptions, PopoverContentComponentProps, PopoverPlacement } from './types';

export interface IPopoverProps {
  placement?: PopoverPlacement;
  open?: boolean;
  setOpen?(value: boolean | ((value: boolean) => boolean)): void;
  showArrow?: boolean;
  flip?: Partial<FlipOptions>;
  variant?: 'info' | 'tooltip' | 'danger' | 'none';
  // This is to handle cases where the child element doesn't forward the ref given to him to the actual DOM, so we will wrap it with a span.
  wrapChildWithRef?: boolean;
  overflow?: boolean;
  onClose?(): void;
  stopPropagation?: boolean;
  trigger?: 'hover' | 'click';
  disabled?: boolean;
  closeDelayInMs?: number;
  openDelayInMs?: number;
  appendToBody?: boolean;
  topOffset?: number;
  children: ReactNode;
  offset?: number;
  zIndex?: number;
  appendToSelector?: string;
  onOpen?: () => void;
  subject?: string;
  overflowable?: boolean;
  transitionDurationMs?: number;
  Content: {
    Component: React.FC<PopoverContentComponentProps>;
    props?: Record<string, any>;
  };
}

export type PopoverHandle = { update: () => void; setPopverOpenState: (value: boolean) => void };

export const Popover = forwardRef<PopoverHandle, IPopoverProps>(
  (
    {
      children,
      placement,
      open,
      setOpen,
      showArrow = false,
      flip = {},
      variant = 'info',
      wrapChildWithRef,
      overflow,
      onClose,
      stopPropagation = false,
      trigger = 'click',
      disabled,
      closeDelayInMs = 1000,
      openDelayInMs = 0,
      appendToBody = false,
      appendToSelector,
      topOffset,
      offset = 0,
      zIndex = 1,
      onOpen,
      Content,
      subject,
      overflowable,
      transitionDurationMs,
    },
    ref,
  ) => {
    const modalContext = useContext(ModalContext);
    // A ref for the original child
    const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
    // A ref for a span-wrapper so we could always have ref to hold on to.
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const { styles, attributes, update, state } = usePopper(
      referenceElement,
      popperElement,
      popoverConfig({
        placement,
        overflow,
        showArrow,
        flip,
        offset,
        overflowable,
      }),
    );

    const stylesWithTopOffset = { ...styles, popper: { ...styles.popper, top: topOffset } };

    const { setIsOpen } = usePopoverState();
    const [isOpenInternal, setIsOpenInternal] = useState<boolean>(false);
    const isOpenRef = useRef(open ?? false);

    // Keep ref in sync with actual open state
    isOpenRef.current = open ?? isOpenInternal;

    const setPopverOpenState = useCallback(
      (newValue: boolean) => {
        if (isOpenRef.current === newValue) return;

        isOpenRef.current = newValue;
        setIsOpen(newValue);
        setIsOpenInternal(newValue);

        (newValue ? onOpen : onClose)?.();
      },
      [setIsOpen, onOpen, onClose],
    );

    const closePopover = useCallback(() => {
      if (setOpen) {
        setOpen(false);
        onClose?.();
      } else {
        setPopverOpenState(false);
      }
    }, [setOpen, onClose, setPopverOpenState]);

    useImperativeHandle(ref, () => ({ update: update!, setPopverOpenState }));

    const openPopover = useCallback(() => {
      if (setOpen) {
        setOpen(true);
        onOpen?.();
      } else {
        setPopverOpenState(true);
      }
    }, [setOpen, onOpen, setPopverOpenState]);

    const togglePopover = useCallback(
      (e: Event) => {
        if (stopPropagation) {
          e.stopPropagation();
        }

        if (isOpenRef.current) {
          closePopover();
        } else {
          openPopover();
        }
      },
      [stopPropagation, openPopover, closePopover],
    );

    usePopoverOnResize({ referenceElement, isOpen: open ?? isOpenInternal, update });

    usePopoverOnHover({
      trigger,
      referenceElement,
      popperElement,
      closePopover,
      openPopover,
      disabled,
      closeDelayInMs,
      openDelayInMs,
    });

    usePopoverOnClick({ trigger, referenceElement, popperElement, closePopover, togglePopover, disabled });

    const child = React.Children.only(children) as any;

    const clonedChild = React.cloneElement(child, {
      ref: setReferenceElement,
    });

    const PopoverElement = (
      <PopoverContainer
        ref={setPopperElement}
        style={topOffset ? stylesWithTopOffset.popper : styles.popper}
        {...attributes.popper}
        variant={variant}
        isTooltip={appendToBody}
        zIndex={zIndex}
        transitionDurationMs={transitionDurationMs}
        {...generateLogzTestAttributes({ context: 'popover', subject })}
      >
        <Content.Component closePopover={closePopover} popoverState={state ?? undefined} {...Content.props} />
        {showArrow && <PopoverArrow style={styles.arrow} data-popper-arrow />}
      </PopoverContainer>
    );

    const getPopoverElement = () => {
      const selector = appendToBody ? 'body' : appendToSelector;

      const targetPortalElement = selector ? document.querySelector(selector) : modalContext;

      if (targetPortalElement) {
        return createPortal(PopoverElement, targetPortalElement);
      }

      return PopoverElement;
    };

    return (
      <>
        {/* We don't care that the ref is assigned both in the clonedChild and the span since React won't invoke that function */}
        {wrapChildWithRef ? <span ref={setReferenceElement}>{clonedChild}</span> : clonedChild}
        {/* If open, check for the need to put the popover on a modal (for overflowing it) or append it to document body (to support tooltip usages) */}
        {(open ?? isOpenInternal) && getPopoverElement()}
      </>
    );
  },
);

Popover.displayName = 'Popover';
